import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import type { ArcInfo } from 'types/arc';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import { getArcHealthModifier } from '../../../utils/arc/healthUtils';
import './ArcSummaryMolecule.scss';

export interface ArcSummaryMoleculeProps {
  arcs: ArcInfo[];
  onNavigateToArc?: (arcId: string) => void;
}

const ArcSummaryMolecule: React.FC<ArcSummaryMoleculeProps> = ({
  arcs,
  onNavigateToArc,
}) => {
  const { t } = useTranslation();

  const sortedArcs = useMemo(() => {
    return [...arcs].sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;
      return 0;
    });
  }, [arcs]);

  const getCurrentStageName = (arc: ArcInfo): string => {
    const stageStatus = arc.stages?.find(s => s.stageId === arc.currentStageId);
    if (!stageStatus) return arc.currentStageId;
    return arc.currentStageId;
  };

  const getMostUrgentAction = (arc: ArcInfo): string | null => {
    const health = arc.health?.status;
    if (health === 'overdue') return t('arcSummary.action.overdue');
    if (health === 'blocked') return t('arcSummary.action.blocked');
    if (health === 'at_risk') return t('arcSummary.action.atRisk');
    if (health === 'attention_needed') return t('arcSummary.action.attentionNeeded');
    return null;
  };

  return (
    <div className="arc-summary">
      <div className="arc-summary__header">
        <i className="pi pi-directions arc-summary__icon" />
        <span className="arc-summary__title">
          {t('arcSummary.title', { count: arcs.length })}
        </span>
      </div>
      <table className="arc-summary__table">
        <thead>
          <tr>
            <th className="arc-summary__th">{t('arcSummary.columns.name')}</th>
            <th className="arc-summary__th">{t('arcSummary.columns.stage')}</th>
            <th className="arc-summary__th">{t('arcSummary.columns.health')}</th>
            <th className="arc-summary__th">{t('arcSummary.columns.action')}</th>
          </tr>
        </thead>
        <tbody>
          {sortedArcs.map((arc) => {
            const healthStatus = arc.health?.status || 'on_track';
            const healthMod = getArcHealthModifier(healthStatus);
            const urgentAction = getMostUrgentAction(arc);

            return (
              <tr
                key={arc.id}
                className="arc-summary__row"
                onClick={() => onNavigateToArc?.(arc.id)}
                role={onNavigateToArc ? 'button' : undefined}
                tabIndex={onNavigateToArc ? 0 : undefined}
                onKeyDown={(e) => {
                  if (onNavigateToArc && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onNavigateToArc(arc.id);
                  }
                }}
              >
                <td className="arc-summary__td arc-summary__td--name">
                  <span className="arc-summary__arc-name">
                    {arc.isPrimary && (
                      <span className="arc-summary__primary-star" title={t('arcSummary.primary')}>★</span>
                    )}
                    {arc.templateSystemName}
                  </span>
                </td>
                <td className="arc-summary__td arc-summary__td--stage">
                  {getCurrentStageName(arc)}
                </td>
                <td className="arc-summary__td arc-summary__td--health">
                  <ChipAtom
                    label={t(`arc.health.${healthStatus}`)}
                    className={healthMod ? `arc-summary__health-chip arc-summary__health--${healthMod}` : 'arc-summary__health-chip '}
                  />
                </td>
                <td className="arc-summary__td arc-summary__td--action">
                  {urgentAction ? (
                    <span className={`arc-summary__action arc-summary__action--${healthMod}`}>
                      {urgentAction}
                    </span>
                  ) : (
                    <span className="arc-summary__no-action">{t('arcSummary.noAction')}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ArcSummaryMolecule;
